import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api from '../api';
import { formatPrice } from '../utils/currency';

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const ytShorts = url.match(/youtube\.com\/shorts\/([^&\n?#]+)/);
  if (ytShorts) return `https://www.youtube.com/embed/${ytShorts[1]}`;
  return url;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [currency, setCurrency] = useState('MAD');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paypalCurrency, setPaypalCurrency] = useState('USD');
  const [cashOnDelivery, setCashOnDelivery] = useState(true);

  const validate = () => {
    const e = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^(\+?212|0)[5-7]\d{8}$/;
    if (!emailRe.test(form.customer_email)) e.customer_email = 'Adresse email invalide.';
    if (!phoneRe.test(form.customer_phone.replace(/\s/g, ''))) e.customer_phone = 'Numéro invalide. Ex: 0612345678 ou +212612345678';
    return e;
  };

  const trackButtonClick = (buttonType) => {
    api.post('/api/clicks', { product_id: id, button_type: buttonType }).catch(() => {});
  };

  useEffect(() => {
    api.get(`/api/products/${id}`)
      .then(({ data }) => {
        setProduct(data.data);
        if (data.data.images && data.data.images.length > 0) {
          setSelectedImg(data.data.images[0].image_url);
        } else if (data.data.image_url) {
          setSelectedImg(data.data.image_url);
        }
      })
      .catch(() => navigate('/products'))
      .finally(() => setLoading(false));

    api.get('/api/settings')
      .then(({ data }) => {
        setCurrency(data.data.currency || 'MAD');
        setWhatsappNumber((data.data.whatsapp_number || '').replace(/\s|\+|-/g, ''));
        setPaypalClientId(data.data.paypal_client_id || '');
        setPaypalCurrency(data.data.paypal_currency || 'USD');
        setCashOnDelivery(data.data.cash_on_delivery === '1');
      })
      .catch(() => {});
  }, [id, navigate]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleOrder = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    trackButtonClick('card');
    setSubmitting(true);
    setResult(null);
    try {
      await api.post('/api/orders', { ...form, product_id: id, quantity: qty });
      setOrderDetails({ ...form, qty, productName: product.name, price: product.price });
      setResult({ success: true, message: '✅ Commande placée ! Confirmez sur WhatsApp ci-dessous.' });
      setForm({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', notes: '' });
      setQty(1);
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.message || 'Erreur. Réessayez.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loader" style={{ marginTop: '6rem' }}><div className="spinner" /></div>;
  if (!product) return null;

  const outOfStock = product.stock !== null && product.stock <= 0;
  const allImages = product.images && product.images.length > 0
    ? product.images.map(img => img.image_url)
    : product.image_url ? [product.image_url] : [];

  const embedUrl = getEmbedUrl(product.video_url);
  const isMobile = window.innerWidth <= 768;

  return (
    <div className="page">
      <div className="container">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/products')}>
          ← Back to Products
        </button>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '3rem',
          alignItems: 'start'
        }}>
          {/* Galerie photos + vidéo */}
          <div>
            <div style={{ marginBottom: '1rem' }}>
              {showVideo && embedUrl ? (
                <iframe
                  src={embedUrl}
                  title="Product Video"
                  style={{ width: '100%', aspectRatio: '16/9', borderRadius: 'var(--radius)', border: 'none' }}
                  allowFullScreen
                />
              ) : (
                selectedImg
                  ? <img src={selectedImg} alt={product.name}
                      style={{ width: '100%', borderRadius: 'var(--radius)', aspectRatio: '1', objectFit: 'cover' }} />
                  : <div className="product-img-placeholder" style={{ borderRadius: 'var(--radius)' }}>📦</div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${allImages.length + (product.video_url ? 1 : 0) > 0 ? Math.min(allImages.length + 1, 6) : 1}, 1fr)`, gap: 8 }}>
              {allImages.map((img, i) => (
                <img key={i} src={img} alt={`${product.name} ${i+1}`}
                  onClick={() => { setSelectedImg(img); setShowVideo(false); }}
                  style={{
                    width: '100%', aspectRatio: '1', objectFit: 'cover',
                    borderRadius: 6, cursor: 'pointer',
                    border: !showVideo && selectedImg === img ? '2px solid #000' : '2px solid transparent',
                    opacity: !showVideo && selectedImg === img ? 1 : 0.7,
                    transition: 'all 0.2s'
                  }} />
              ))}
              {product.video_url && (
                <div
                  onClick={() => setShowVideo(true)}
                  style={{
                    width: '100%', aspectRatio: '1', borderRadius: 6, cursor: 'pointer',
                    border: showVideo ? '2px solid #000' : '2px solid transparent',
                    overflow: 'hidden', position: 'relative', transition: 'all 0.2s'
                  }}>
                  <video
                    src={product.video_url}
                    autoPlay muted loop playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                  />
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: showVideo ? 'rgba(0,0,0,0.4)' : 'transparent'
                  }}>
                    {!showVideo && (
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.85)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <span style={{ fontSize: 14, marginLeft: 3 }}>▶</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.8rem', marginTop: '1.5rem' }}>{product.name}</h1>
            <div className="product-price" style={{ fontSize: '1.5rem', margin: '.5rem 0' }}>
              {formatPrice(product.price, currency)}
            </div>
            {product.description && (
              <p style={{ marginTop: '1rem', color: 'var(--text-muted)', lineHeight: '1.7' }}>{product.description}</p>
            )}
          </div>

          {/* Order Form */}
          <div className="card" style={{ padding: '2rem' }}>
            <h2 style={{ fontFamily: 'var(--font-head)', marginBottom: '1.5rem' }}>Place Your Order</h2>

            {result && (
              <div className={`alert ${result.success ? 'alert-success' : 'alert-error'}`}>
                {result.message}
              </div>
            )}

            {result?.success && orderDetails && whatsappNumber && (
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                  `🛍️ *Nouvelle commande*\n\n` +
                  `📦 Produit : ${orderDetails.productName}\n` +
                  `🔢 Quantité : ${orderDetails.qty}\n` +
                  `💰 Total : ${formatPrice(parseFloat(orderDetails.price) * orderDetails.qty, currency)}\n\n` +
                  `👤 Nom : ${orderDetails.customer_name}\n` +
                  `📞 Téléphone : ${orderDetails.customer_phone}\n` +
                  `📧 Email : ${orderDetails.customer_email}\n` +
                  `📍 Adresse : ${orderDetails.customer_address}\n` +
                  (orderDetails.notes ? `📝 Notes : ${orderDetails.notes}\n` : '') +
                  `\nMerci de confirmer ma commande !`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.6rem',
                  background: '#25D366', color: 'white', fontWeight: 700,
                  padding: '1rem', borderRadius: 8, textDecoration: 'none',
                  fontSize: '1rem', marginBottom: '1rem',
                  boxShadow: '0 4px 15px rgba(37,211,102,0.35)',
                }}
              >
                <svg viewBox="0 0 32 32" width="22" height="22" fill="white">
                  <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.469 2.027 7.77L0 32l8.44-2.01A15.94 15.94 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm7.27 19.463c-.398-.199-2.354-1.162-2.719-1.294-.365-.133-.631-.199-.897.199-.265.398-1.03 1.294-1.263 1.56-.232.265-.465.298-.863.1-.398-.2-1.68-.619-3.2-1.974-1.183-1.055-1.981-2.358-2.213-2.756-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.698.199-.232.265-.398.398-.663.133-.266.066-.499-.033-.698-.1-.2-.897-2.162-1.23-2.96-.323-.778-.652-.673-.897-.685l-.764-.013c-.265 0-.696.1-1.061.499-.365.398-1.394 1.362-1.394 3.323 0 1.961 1.427 3.857 1.626 4.123.199.265 2.808 4.287 6.805 6.013.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.765-.114 2.354-.962 2.686-1.89.332-.929.332-1.726.232-1.89-.099-.166-.365-.265-.763-.465z"/>
                </svg>
                Confirmer ma commande sur WhatsApp
              </a>
            )}

            <form onSubmit={handleOrder}>
              <div className="form-group">
                <label>Full Name *</label>
                <input name="customer_name" value={form.customer_name} onChange={handleChange}
                  className="form-control" placeholder="Your full name" required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input name="customer_email" type="email" value={form.customer_email} onChange={handleChange}
                  className="form-control" placeholder="your@email.com" required
                  style={errors.customer_email ? { borderColor: 'var(--danger)' } : {}} />
                {errors.customer_email && <p style={{ color: 'var(--danger)', fontSize: '.8rem', marginTop: '.25rem' }}>{errors.customer_email}</p>}
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input name="customer_phone" value={form.customer_phone} onChange={handleChange}
                  className="form-control" placeholder="0612345678 ou +212612345678" required
                  style={errors.customer_phone ? { borderColor: 'var(--danger)' } : {}} />
                {errors.customer_phone && <p style={{ color: 'var(--danger)', fontSize: '.8rem', marginTop: '.25rem' }}>{errors.customer_phone}</p>}
              </div>
              <div className="form-group">
                <label>Delivery Address *</label>
                <textarea name="customer_address" value={form.customer_address} onChange={handleChange}
                  className="form-control" placeholder="Rue, Ville, Pays" required rows={3} />
              </div>
              <div className="form-group">
                <label>Quantity</label>
                <input type="number" min="1" max={product.stock || 999}
                  value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)}
                  className="form-control" style={{ maxWidth: '100px' }} />
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea name="notes" value={form.notes} onChange={handleChange}
                  className="form-control" placeholder="Instructions spéciales ?" rows={2} />
              </div>

              <div style={{ background: '#f8f7f4', padding: '1rem', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span>Total</span>
                  <span>{formatPrice(parseFloat(product.price) * qty, currency)}</span>
                </div>
              </div>

              {cashOnDelivery && (
                <button type="submit" className="btn btn-primary btn-full" disabled={submitting || outOfStock}>
                  {submitting ? 'Placing order…' : outOfStock ? 'Out of Stock' : '📦 Commander (paiement à la livraison)'}
                </button>
              )}
            </form>

            {paypalClientId && !outOfStock && !result?.success && (
              <div style={{ marginTop: '1rem' }}>
                {cashOnDelivery && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1rem 0' }}>
                    <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e5e7eb' }} />
                    <span style={{ color: '#aaa', fontSize: '.85rem', whiteSpace: 'nowrap' }}>ou payer en ligne</span>
                    <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e5e7eb' }} />
                  </div>
                )}
                <PayPalScriptProvider options={{ 'client-id': paypalClientId, currency: paypalCurrency }}>
                  <PayPalButtons
                    style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                    disabled={outOfStock}
                    onClick={(data, actions) => {
                      // Tracker le clic sur n'importe quel bouton PayPal (PayPal ou Carte bancaire)
                      if (data.fundingSource === 'paypal') {
                        trackButtonClick('paypal');
                      } else {
                        trackButtonClick('card');
                      }
                      return actions.resolve();
                    }}
                    createOrder={async () => {
                      const { data } = await api.post('/api/paypal/create-order', {
                        amount: (parseFloat(product.price) * qty).toFixed(2),
                        currency: paypalCurrency,
                        description: `${product.name} x${qty}`,
                      });
                      return data.orderId;
                    }}
                    onApprove={async (paypalData) => {
                      const capture = await api.post(`/api/paypal/capture-order/${paypalData.orderID}`);
                      if (capture.data.success) {
                        await api.post('/api/orders', {
                          ...form,
                          product_id: id,
                          quantity: qty,
                          notes: `[PayPal] ${form.notes || ''} — TX: ${paypalData.orderID}`,
                        });
                        setOrderDetails({ ...form, qty, productName: product.name, price: product.price });
                        setResult({ success: true, message: '✅ Paiement PayPal réussi ! Commande confirmée.' });
                      }
                    }}
                    onError={() => setResult({ success: false, message: '❌ Erreur PayPal. Réessayez.' })}
                  />
                </PayPalScriptProvider>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;